import { Component, forwardRef, input } from '@angular/core';
import { UntypedFormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormOption } from 'src/app/shared/models/form-option.model';
import {
  WNL_HOST_CONTROL,
  WnlHostControl,
  WnlHostType,
} from 'src/app/shared/tokens/wnl-host-control.token';
import { NgClass } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { InputNumberModule } from 'primeng/inputnumber';
import { Select } from 'primeng/select';
import { DatePicker } from 'primeng/datepicker';
import { CheckboxModule } from 'primeng/checkbox';

/**
 * Customizable input used throughout the app (but mostly for the Nursing Assessments tab).
 * Input can be rendered as a text, textarea, numerical, select, or date input.
 * To see examples where this component is used, visit the any of the classes that extend the
 * AssessmentFormComponent class (e.g. Neurological, Gastrointestinal, Cardiovascular).
 */
@Component({
  selector: 'emr-input',
  templateUrl: './emr-input.component.html',
  styleUrls: ['./emr-input.component.scss'],
  standalone: true,
  providers: [
    {
      provide: WNL_HOST_CONTROL,
      useExisting: forwardRef(() => EmrInputComponent),
    },
  ],
  imports: [
    NgClass,
    FormsModule,
    InputTextModule,
    ReactiveFormsModule,
    TextareaModule,
    InputNumberModule,
    Select,
    DatePicker,
    CheckboxModule
],
})
export class EmrInputComponent implements WnlHostControl {
  /**
   * The type of input to display.
   *
   * Available options: "text", "textarea", "number", "decimal", "select", "date", "time"
   */
  type = input<'text' | 'textarea' | 'number' | 'decimal' | 'select' | 'date' | 'time'>('text');

  /**
   * Controls the width of the p-input element that is displayed.
   */
  width = input('100%');

  /**
   * Label displayed above the input field that describes what the input is for.
   */
  label = input('');

  /**
   * Text displayed within the input field when nothing has been entered.
   */
  placeholder = input('');

  /**
   * Hint that appears under the input box.
   */
  hint = input<string>();

  /**
   * The FormControl to get/set the value for.
   */
  control = input<UntypedFormControl>(new UntypedFormControl());

  /**
   * The minimum value that can be entered in the input.
   * Only used with the "number" and "decimal" types.
   */
  min = input(0);

  /**
   * The maximum value that can be entered in the input.
   * Only used with the "number" and "decimal" types.
   */
  max = input(9999999999);

  /**
   * The options to display in the list the user can select from.
   * Only used with the "select" type.
   */
  options = input<FormOption[]>([]);

  /**
   * The text to display in the p-dropdown if there are no options.
   * Only used with the "select" type.
   */
  emptyMessage = input('No data');

  /**
   * Whether the input is a required input. This is used primarily to
   * display a '*' character next to the label above the input field
   * to indicate it is required.
   */
  required = input(false);

  /**
   * Whether the component should have a blank space below the input field.
   * This is used primarily to align the input field with other input fields
   * that have hints displayed on the bottom.
   */
  bottomSpace = input(false);

  /**
   * Event handler that is called whenever the input has been changed,
   * if the input is the "select" type.
   *
   * @param event The value that the select input has been changed to.
   */
  selectionChanged(event: any) {
    // If "Select One" option is selected, clear the field
    if (event.value === '__reset__') {
      this.control()?.setValue('', { onlySelf: true });
      this.control()?.markAsPristine();
      this.control()?.markAsUntouched();
    }
  }

  get hasHint() {
    return this.hint() !== undefined && this.hint() !== null && this.hint() !== '';
  }

  get hasBottomSpace() {
    return this.bottomSpace();
  }
  get selectOptions(): FormOption[] {
    if (this.type() === 'select') {
      const selectOneOption = new FormOption('Select One', '__reset__');
      return [selectOneOption, ...this.options()];
    }
    return this.options();
  }

  getWnlControl(): UntypedFormControl | null {
    return this.control();
  }

  getWnlHostType(): WnlHostType {
    return 'input';
  }

  isWnlSelectInput(): boolean {
    return this.type() === 'select';
  }
}
